import 'leaflet/dist/leaflet.css';

import { Polyline, TileLayer, MapContainer } from 'react-leaflet';
import { PlanningsMapGaragePositionEntity } from 'src/entities/PlanningsMapGaragePosition.entity';
import { PositionEntity } from 'src/entities/PositionEntity';
import PlanningMapCurrentPositionsPresenter from 'src/pages/V3Plans/presenters/PlanningMapCurrentPositions.presenter';
import PlanningMapGarageMarkersPresenter from 'src/pages/V3Plans/presenters/PlanningMapGarageMarkers.presenter';

import { GroupedPinContext } from '../clusterEvents';
import { DISPLAY_KIND_OPTIONS, showsGarage } from '../mapDisplayKinds';
import { MapRouteMarkerVM, MapUnassignedMarkerVM, MapVehicleRouteVM } from '../mapVM';
import { UNASSIGNED_POLYLINE_COLOR, UnassignedPolyline } from '../unassignedPolylines';
import { UnassignedTooltipVM } from '../unassignedTooltip';
import { resolveVisibleRoutes } from '../visibleRoutes';


import MapBackgroundClick from './MapBackgroundClick.presenter';
import MapFitBounds from './MapFitBounds.presenter';
import MapInvalidateSize from './MapInvalidateSize.presenter';
import UnassignedMarkerCluster from './UnassignedMarkerCluster.presenter';
import VehicleRouteMarkers from './VehicleRouteMarkers.presenter';

type Props = {
  routes: MapVehicleRouteVM[];
  unassignedMarkers: MapUnassignedMarkerVM[];
  /** 選択中の未割当案件の 積地→降地 の線。空なら描かない */
  unassignedPolylines?: UnassignedPolyline[];
  /** 右ペインの開閉状態。変わったら地図のサイズを取り直す */
  rightPanelOpen?: boolean;
  /** 左右リストの幅。ドラッグで変わると地図の描画領域も変わるのでサイズを取り直す */
  leftPanelWidth?: number;
  rightPanelWidth?: number;
  /** 地図に出す種類（積地 / 納品地 / 車庫）。未指定なら全部出す */
  displayKinds?: readonly string[];
  /** 車両の現在地を出すか。車両位置のライセンスがある会社だけ true になりうる */
  showTruckStatus?: boolean;
  /** 現在地の取得範囲。showTruckStatus のときだけ使う */
  startOn?: string;
  endOn?: string;
  garages: PlanningsMapGaragePositionEntity[];
  selectedRowIds: Set<string>;
  onMarkerRowClick: (rowId: string) => void;
  /** マーカー・未割当ピンから詳細パネルを開く配線。 */
  onMarkerClick?: (rowId: string, marker: MapRouteMarkerVM) => void;
  /** context は集合状態（クラスター / 同一地点に複数案件）のときだけ渡る */
  onUnassignedClick?: (id: string, context?: GroupedPinContext) => void;
  /**
   * 未割当ピンのホバーに出す内容（マーカーの代表 id 引き）。詳細カードはクリックで出すので、
   * ホバーはこの軽い表示だけにする（サンプル MapView.tsx:186-224 と同じ切り分け）。
   */
  unassignedTooltips?: ReadonlyMap<string, UnassignedTooltipVM>;
  /** 右クリックで選択解除（旧地図と同じ操作） */
  onMarkerRowDeselect?: (rowId: string) => void;
  onUnassignedDeselect?: (id: string) => void;
  /** 地図の何も無いところが押されたとき。開いているパネルを閉じるのに使う */
  onBackgroundClick?: () => void;
  /** 選択中の未割当案件。未選択のピンを薄くするのに使う */
  selectedUnassignedIds?: ReadonlySet<string>;
  /** データ取得中は初回 fitBounds を待たせる（途中の一部だけで固定されるのを防ぐ） */
  isLoading?: boolean;
};

const MapView = ({
  routes,
  unassignedMarkers,
  unassignedPolylines = [],
  rightPanelOpen,
  leftPanelWidth,
  rightPanelWidth,
  displayKinds,
  showTruckStatus,
  startOn,
  endOn,
  garages,
  selectedRowIds,
  onMarkerRowClick,
  onMarkerClick,
  onUnassignedClick,
  unassignedTooltips,
  onMarkerRowDeselect,
  onUnassignedDeselect,
  onBackgroundClick,
  selectedUnassignedIds,
  isLoading = false,
}: Props) => {
  // 未指定なら全部出す（フィルタを渡さない呼び出し元の既定）
  const kinds = displayKinds ?? DISPLAY_KIND_OPTIONS;
  // 未割当だけを選んだときも、配車済みの点は「選んでいない」ので薄くする
  const hasSelection = selectedRowIds.size > 0 || (selectedUnassignedIds?.size ?? 0) > 0;
  // 線は選択した車両のぶんだけ描く。全車両を重ねると経路が読めない（指示 47）
  const visibleRouteIds = new Set(resolveVisibleRoutes(routes, selectedRowIds).map((r) => r.rowId));

  // 初回 fitBounds の対象。ルート・未割当が無く車庫だけの日でも地図が寄るよう車庫座標も含める。
  const allPositions: PositionEntity[] = routes
    .flatMap((r) => r.positions)
    .concat(unassignedMarkers.map((m) => m.position))
    .concat(garages.map((g) => g.position));

  return (
    <MapContainer
      style={{ width: '100%', height: '100%' }}
      center={[35.6895, 139.6917]}
      zoom={11}
      scrollWheelZoom
      zoomControl={false}
    >
      <TileLayer
        attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <MapFitBounds positions={allPositions} isReady={!isLoading} />
      {onBackgroundClick && <MapBackgroundClick onBackgroundClick={onBackgroundClick} />}
      {/* 右ペインを閉じて地図が広がっても、測り直さないと広がった分が空白になる */}
      {/* 幅の変化はドラッグ中に連続で来るが、MapInvalidateSize は watch が変わるたびに
          タイマーを張り直すので、実際に測るのは手を離してからの 1 回だけになる */}
      <MapInvalidateSize watch={`${rightPanelOpen}:${leftPanelWidth}:${rightPanelWidth}`} />

      {/* Why not 点線: 地図を縮めると破線の隙間が地点の間隔を超えて線が消え、
          どの順で回るのかが読めなくなる（指摘 2026/08/18）。 */}
      {routes
        .filter((route) => visibleRouteIds.has(route.rowId) && route.positions.length >= 2)
        .map((route) => (
          <Polyline
            key={route.rowId}
            positions={route.positions}
            pathOptions={{ color: route.color, opacity: 0.7 }}
          />
        ))}

      {/* 番号マーカーと未割当ピンは leaflet を直接操作して差分だけ反映する
          （宣言的な <Marker> は再描画・出し入れのたびに全本を触る。
          logposeTech/log_web_issues#321）。 */}
      <VehicleRouteMarkers
        routes={routes}
        kinds={kinds}
        selectedRowIds={selectedRowIds}
        hasSelection={hasSelection}
        onMarkerRowClick={onMarkerRowClick}
        onMarkerClick={onMarkerClick}
        onMarkerRowDeselect={onMarkerRowDeselect}
      />

      <UnassignedMarkerCluster
        markers={unassignedMarkers}
        tooltips={unassignedTooltips}
        hasSelection={hasSelection}
        selectedUnassignedIds={selectedUnassignedIds}
        onUnassignedClick={onUnassignedClick}
        onUnassignedDeselect={onUnassignedDeselect}
      />

      {/* 選択した未割当案件の 積地→降地。点だけでは「どこからどこへ運ぶか」が読めない
          (V3Plans presenters/OrderPolylinePresenter.tsx:38-45 と同じ見た目) */}
      {unassignedPolylines.map((line) => (
        <Polyline
          key={`unassigned-${line.id}`}
          positions={line.positions}
          pathOptions={{ color: UNASSIGNED_POLYLINE_COLOR, weight: 5, opacity: 1 }}
        />
      ))}

      {/* 車庫は /plans と同じ presenter（グレーPNGピン＋実名Tooltip）で描画する。 */}
      {showsGarage(kinds) && <PlanningMapGarageMarkersPresenter entities={garages} />}

      {/* 車両の現在地。旧地図配車と同じ presenter を使う（V3Plans/Presenter.tsx:1116-1120） */}
      {showTruckStatus && startOn && endOn && (
        <PlanningMapCurrentPositionsPresenter startOn={startOn} endOn={endOn} />
      )}
    </MapContainer>
  );
};

export default MapView;
